import { validateClausebookPick } from './clausebook_pick.js';
import {
  hasLiftedClauseHtml,
  rememberLiftedClauseHtml,
} from './clausebook_insert.js';

function responseData(response) {
  return (response && response.data) || response || {};
}

export function clausebookRetrievalWarranted(proposal) {
  // Mirrors the server's clausebook_retrieval_warranted decision; the server
  // remains authoritative because it computed contentProvenance after stripping
  // model-authored annotations.
  return Boolean(
    proposal &&
      typeof proposal === 'object' &&
      ['adapted', 'drafted'].includes(proposal.contentProvenance),
  );
}

function words(value) {
  return new Set(
    String(value || '')
      .toLowerCase()
      .match(/[a-z0-9]+/g) || [],
  );
}

function overlap(value, evidence) {
  return Array.from(words(value)).filter(
    word => word.length > 2 && evidence.has(word),
  ).length;
}

// Which source concept a proposal is about, or none.
//
// Scored over `name` and `summary`, which is the whole of what a source concept
// carries: `normalize_source_concepts` (services_controller.rb) builds each one
// as { name, summary, group } and nothing downstream adds to it. A `description`
// is the CONTROLLED vocabulary's shape and is never present here.
//
// The NAME has to match for the concept to qualify at all, and the summary only
// ranks the ones that do. Summaries are written in lease prose, so scoring them
// on their own makes "tenant", "landlord" or "premises" enough to associate a
// cure-period clause with parking rights — which then asks Algolia the wrong
// question and books the retrieval against the wrong concept. A concept's
// two-to-five-word name is the term itself, so requiring one of its words is the
// difference between "this proposal is about that term" and "both are about a
// lease".
export function conceptForProposal(proposal, concepts) {
  const evidence = words(
    [proposal.sourceQuote, proposal.rationale, proposal.content].join(' '),
  );

  return (concepts || []).reduce(
    (best, concept) => {
      if (!overlap(concept && concept.name, evidence)) return best;

      const score = overlap(
        [concept && concept.name, concept && concept.summary].join(' '),
        evidence,
      );
      return score > best.score ? { concept, score } : best;
    },
    { concept: null, score: 0 },
  ).concept;
}

function validEntry(entry) {
  return (
    entry &&
    typeof entry.id === 'string' &&
    entry.id.trim() &&
    typeof entry.name === 'string' &&
    entry.name.trim() &&
    typeof entry.url === 'string' &&
    entry.url.trim()
  );
}

async function retrieveForProposal({ proposal, concept, documentId, api }) {
  if (!clausebookRetrievalWarranted(proposal) || !concept) return proposal;

  try {
    const offer = responseData(
      await api.clausebookCandidates({ documentId, concept, proposal }),
    );
    const candidates = Array.isArray(offer.candidates) ? offer.candidates : [];
    const retrievalToken = offer.retrievalToken;
    if (!retrievalToken) return proposal;

    const rawPick = candidates.length === 0 ? { selected: null } : offer.pick;
    const pick = validateClausebookPick(rawPick, candidates);
    // A malformed or missing model answer is an integration failure, not a
    // deliberate decline, and must not pollute the coverage-gap observation.
    if (!pick.valid) return proposal;

    if (pick.value.selected === null) {
      await api.recordClausebookRetrieval({
        documentId,
        retrievalToken,
        selected: null,
        reason: pick.value.reason,
      });
      return proposal;
    }

    const bodyResponse = responseData(
      await api.clausebookBodies({
        documentId,
        retrievalToken,
        candidatePositions: [pick.value.selected],
      }),
    );
    const selectedBody = Array.isArray(bodyResponse.bodies)
      ? bodyResponse.bodies[0]
      : null;
    if (!selectedBody || typeof selectedBody.body !== 'string') return proposal;
    if (!validEntry(bodyResponse.clausebookEntry)) return proposal;

    const liftedProposal = rememberLiftedClauseHtml(
      {
        ...proposal,
        content: selectedBody.body,
        contentProvenance: 'lifted',
        clausebookEntry: bodyResponse.clausebookEntry,
      },
      bodyResponse.html,
    );
    // A lift without usable HTML would silently fall back to the plain-text
    // patch path and claim Clausebook provenance for flattened language.
    if (!hasLiftedClauseHtml(liftedProposal)) return proposal;

    // Complete the privacy-bounded observation before publishing attribution.
    // If the write fails, the optional enhancement fails closed and the
    // proposal remains exactly as it arrived.
    await api.recordClausebookRetrieval({
      documentId,
      retrievalToken,
      selected: pick.value.selected,
      reason: pick.value.reason,
    });

    return liftedProposal;
  } catch (_error) {
    // Do not include the Angular error object: its request config can contain
    // proposal language, and console output is captured by LogRocket.
    console.warn(
      '[clausebookRetrieval] unavailable; keeping the drafted proposal',
    );
    return proposal;
  }
}

export async function enrichProposalsWithClausebook({
  proposals,
  concepts,
  documentId,
  api,
}) {
  if (!Array.isArray(proposals)) return proposals;

  return Promise.all(
    proposals.map(proposal =>
      retrieveForProposal({
        proposal,
        concept: conceptForProposal(proposal, concepts),
        documentId,
        api,
      }),
    ),
  );
}
